import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  EditableSlotMediaOverlay,
  getSlotDef,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
  useMarqueeProximityRate,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import HeroGalleryStrip from "./HeroGalleryStrip";
import { EditContext } from "../../../editor/context/EditContext";
import { apiFetch } from "../../../core/utils/api";
import "./heroModeAnimations.css";
import "./heroGallery.css";

/**
 * HeroGallery — renderer for ``gallery`` (static) and
 * ``gallery_animated`` (marquee) modes.
 *
 * Lays out a text column alongside an image track — directly inspired
 * by the landing page's isometric featured-websites track. The ambient
 * marquee comes from the shared ``hero-ambient-marquee-y`` class (see
 * ``heroModeAnimations.css``), which expects a duplicated child list
 * so the seam lands on an identical copy.
 *
 * Gating is entirely mode-driven: the animated variant declares
 * ``ambient_animation = "marquee-y"`` so ``useHeroAmbient`` returns the
 * marquee class; the static variant declares ``ambient_animation = None``
 * so ``useHeroAmbient`` returns "" and the track renders as a plain
 * isometric stack. Reduced-motion users always see the static form
 * regardless of which variant was picked.
 *
 * The gallery column accepts up to 8 individual ``image`` items (see
 * ``GalleryMode.slots`` on the backend — ``image_collection`` is no
 * longer auto-assigned to this slot so the editor can offer single-click
 * "Add Image" creation). Images are normalised into a flat
 * ``[{image_url, cropped_image_url, alt_text, id}, ...]`` list below.
 * ``HeroSerializer.get_children`` embeds the URL fields on the minimal
 * refs for media types, so this renderer doesn't round-trip through
 * ``/api/items/...`` in the common case.
 *
 * Legacy heroes may still have ``image_collection`` refs in the
 * gallery slot (e.g. user switched into Gallery mode before this
 * change). The fallback fetch below handles them defensively so those
 * sites keep rendering correctly until the user re-curates their
 * gallery.
 */

// ---------------------------------------------------------------------------
// Breakpoint config
// ---------------------------------------------------------------------------
//
// Pattern matches HeroSplit: horizontal on desktop, stacked on mobile, with
// per-breakpoint padding and gallery widths. The ``trackDuration`` controls
// how fast the marquee loop completes — we slow it down on larger screens
// because more cards are visible so each one needs longer airtime.
const BP_CONFIG = {
  extra_large: { horizontal: true, mediaPct: "50%", contentPx: 64, cardW: 420, cardH: 240, gap: 48, trackDuration: 45 },
  large:       { horizontal: true, mediaPct: "50%", contentPx: 56, cardW: 380, cardH: 220, gap: 40, trackDuration: 40 },
  medium:      { horizontal: true, mediaPct: "45%", contentPx: 40, cardW: 320, cardH: 190, gap: 32, trackDuration: 35 },
  small:       { horizontal: false, contentPx: 24, contentPy: 32, cardW: 220, cardH: 130, gap: 20, trackDuration: 30 },
  extra_small: { horizontal: false, contentPx: 16, contentPy: 24, cardW: 180, cardH: 110, gap: 16, trackDuration: 28 },
};

// Minimum number of logical items before we duplicate for a seamless loop.
// With fewer than 3 images the marquee effect is visually pointless (it
// just scrolls past a tiny strip with huge gaps), so we stop there and
// render a static stack instead.
const MIN_ITEMS_FOR_MARQUEE = 3;

// ---------------------------------------------------------------------------
// Gallery track (internal)
// ---------------------------------------------------------------------------
//
// Renders either the marqueeing track (isometric 3D, duplicated children)
// or a static stacked grid depending on the ``ambientClass`` presence.
// Everything layout/pixel-related lives on the inline style so the CSS
// file can stay focused on animation keyframes + 3D transforms.

// Default card aspect ratio (width ÷ height) used before an image's
// natural dimensions are known, and as the fallback for any source we
// can't measure. Matches the breakpoint config's wide rectangle (e.g.
// 420×240 ≈ 1.75:1 at extra-large) so the very first paint looks the
// same as the legacy fixed-frame layout, then each card snaps to its
// image's true ratio on load.
const defaultCardRatio = (config) => config.cardW / config.cardH;

// Resolve a card's aspect ratio (width ÷ height) from a measured image
// ratio, falling back to the breakpoint default when we haven't parsed
// the source's natural dimensions yet. Every card keeps the same
// width, so a portrait/long image (ratio < 1) simply produces a taller
// card and a wide landscape produces a shorter one — the image is
// never cropped to fit a fixed frame.
const cardAspectRatio = (ratio, config) =>
  typeof ratio === "number" && ratio > 0 ? ratio : defaultCardRatio(config);

function GalleryTrack({
  images,
  ambientClass,
  config,
  isHorizontal,
  isFlush,
  addImageCard = null,
  trackRef = null,
  heroItem = null,
}) {
  // No ambient motion → plain static grid. Still uses the isometric scene
  // so the mode's signature look is preserved even for reduced-motion
  // users (they see the cards, they just don't scroll).
  const staticMode =
    !ambientClass || !images?.length || images.length < MIN_ITEMS_FOR_MARQUEE;

  // ─── Per-image aspect ratio tracking ───────────────────────────────
  //
  // We swap the card frame to a square (``cardH × cardH``) for portrait
  // sources so the photograph stays readable inside the gallery, but
  // we don't know the natural dimensions until the browser has parsed
  // the image. Backend serializers don't embed width/height on the
  // gallery's minimal image refs (see
  // ``HeroSerializer._build_minimal_child_ref``), and adding them
  // there would force a database round-trip on every hero payload
  // without changing the runtime story — the renderer would still
  // need to handle "no dimensions yet" for legacy rows that lack the
  // metadata anyway. So we read ``naturalWidth`` / ``naturalHeight``
  // from the ``<img>`` element on its first ``onLoad`` and cache the
  // ratio keyed by ``src`` (URL). Keying by URL — not by card key —
  // means the marquee's duplicated cards share a single measurement
  // and we don't briefly render the same image at two different
  // sizes while later copies resolve.
  const [imageAspectRatios, setImageAspectRatios] = useState({});
  const handleImageLoad = (src) => (event) => {
    if (!src) return;
    const node = event.currentTarget;
    if (!node || !node.naturalWidth || !node.naturalHeight) return;
    const ratio = node.naturalWidth / node.naturalHeight;
    setImageAspectRatios((prev) => {
      if (prev[src] !== undefined) return prev;
      return { ...prev, [src]: ratio };
    });
  };

  // ─── Infinite-loop sizing ──────────────────────────────────────────
  // The marquee keyframe translates the track by ``-50%`` of its own
  // length (Y for the desktop "vertical-scroll" track, X for the
  // mobile "horizontal-scroll" track) and snaps back. For the loop to
  // read as truly infinite (no visible "end of the gallery") two
  // things have to be true:
  //
  //   1. The track must be anchored to the *leading edge* of the
  //      scene on the scroll axis (top for vertical-scroll, left for
  //      horizontal-scroll), so the visible window starts at track
  //      position ``0`` at t=0 and reaches ``halfStride`` at t=T.
  //      The scene's ``align-items`` / ``justify-content`` rules in
  //      ``heroGallery.css`` enforce this — see
  //      ``.hero-gallery-scene--horizontal`` /
  //      ``.hero-gallery-scene--vertical``. Anchoring at ``center``
  //      instead leaves the visible window centred on the track's
  //      midpoint, and as the keyframe progresses to ``-50%`` the
  //      bottom edge of the visible window slides past the duplicated
  //      half and reveals a strip of bare hero background — the
  //      previously user-reported "the images end and the new ones
  //      get generated after there is empty space" symptom.
  //
  //   2. The *first half* of the track (cards 1..N×repsPerHalf) must
  //      itself be at least as long as the visible scene. Otherwise
  //      even with the correct anchor the loop terminus tries to
  //      show content past the duplicated half.
  //
  // (1) is a CSS concern; (2) is the math below.
  //
  // Measure the scene at runtime instead of trusting the breakpoint
  // config, because the actual height/width depends on the surrounding
  // hero composition (responsive height + how much text the user
  // wrote, sibling columns, etc.) and only the DOM knows the truth.
  const sceneRef = useRef(null);
  // Scroll-axis size: scene height for the desktop "vertical-scroll"
  // track (``isHorizontal=true``), scene width for the mobile
  // "horizontal-scroll" track (``isHorizontal=false``). Naming kept
  // generic on purpose so the rest of the math reads symmetrically.
  const [sceneAxisPx, setSceneAxisPx] = useState(0);
  // ``useLayoutEffect`` runs synchronously after DOM mutations and
  // *before* the browser paints. We use it (instead of ``useEffect``)
  // so the very first paint already reflects the measured scene size
  // — otherwise the initial frame would be rendered with
  // ``sceneAxisPx === 0`` and ``repsPerHalf`` would fall back to ``1``
  // for one paint, briefly leaving the track too short to cover taller
  // heroes (visible as a flash of bare background at the bottom of
  // the gallery column on first reveal).
  useLayoutEffect(() => {
    const node = sceneRef.current;
    if (!node) return undefined;
    const update = () =>
      setSceneAxisPx(isHorizontal ? node.offsetHeight || 0 : node.offsetWidth || 0);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isHorizontal]);

  // Single-set length along the scroll axis: N cards + (N-1) gaps.
  // Every card shares the same width (``config.cardW``); height is
  // derived from the image's true aspect ratio so portrait/long
  // sources are taller and landscape sources shorter — none are
  // cropped. Vertical-scroll therefore measures each card's
  // ratio-derived height (``cardW / ratio``); horizontal-scroll
  // measures the shared, constant width.
  const trackGap = isFlush ? 0 : config.gap;
  const singleSetPx = useMemo(() => {
    if (!images?.length) return 0;
    const cardsPx = images.reduce((sum, img) => {
      const src = img?.cropped_image_url || img?.image_url;
      const ratio = src ? imageAspectRatios[src] : undefined;
      const aspect = cardAspectRatio(ratio, config);
      const cardLen = isHorizontal ? config.cardW / aspect : config.cardW;
      return sum + cardLen;
    }, 0);
    return cardsPx + Math.max(0, images.length - 1) * trackGap;
  }, [images, imageAspectRatios, isHorizontal, config, trackGap]);

  // ``repsPerHalf`` = how many copies of the image set fit inside one
  // half of the track. Defaults to 1 until we've measured (initial
  // paint) so the very first frame matches the legacy "duplicate
  // once" behaviour and we don't over-render the common
  // few-images-on-small-viewport case.
  const repsPerHalf = useMemo(() => {
    if (staticMode || singleSetPx === 0 || sceneAxisPx === 0) return 1;
    return Math.max(1, Math.ceil(sceneAxisPx / singleSetPx));
  }, [staticMode, singleSetPx, sceneAxisPx]);

  // Build the track. Total copies = ``2 × repsPerHalf`` (always even,
  // so the ``-50%`` seam splits the track into two visually identical
  // halves). For the static branch we render a single copy — the
  // marquee isn't running so duplicates would just look like extra
  // photos to the user.
  const trackItems = useMemo(() => {
    if (!images?.length) return [];
    if (staticMode) return images.map((img, i) => ({ img, key: `g-${img.id || i}-${i}` }));
    const out = [];
    const totalCopies = 2 * repsPerHalf;
    for (let copy = 0; copy < totalCopies; copy++) {
      images.forEach((img, i) => {
        out.push({ img, key: `g-c${copy}-${img.id || i}-${i}` });
      });
    }
    return out;
  }, [images, staticMode, repsPerHalf]);

  if (!trackItems.length && !addImageCard) return null;

  return (
    <div
      ref={sceneRef}
      className={`hero-gallery-scene ${isHorizontal ? "hero-gallery-scene--horizontal" : "hero-gallery-scene--vertical"}`}
    >
      <div
        ref={trackRef}
        className={`hero-gallery-track ${ambientClass} ${
          staticMode ? "hero-gallery-track--static" : ""
        } ${isHorizontal ? "hero-gallery-track--vertical-scroll" : "hero-gallery-track--horizontal-scroll"}`}
        style={{
          gap: `${trackGap}px`,
          "--hero-marquee-seam-offset": `${trackGap / 2}px`,
          "--hero-marquee-duration": `${config.trackDuration}s`,
        }}
      >
        {trackItems.map(({ img, key }) => {
          // ``ImageSerializer`` exposes ``image_url`` (raw) and
          // ``cropped_image_url`` (respects the user's crop). Prefer the
          // cropped variant so the gallery matches what the user sees
          // elsewhere in the editor; fall back to the raw url when no
          // crop is applied. The long-dead ``img.image`` shape pre-dated
          // the cropped-url rollout and was silently rendering empty
          // cards — that's the "images don't show up" symptom users hit
          // in Gallery mode.
          const src = img.cropped_image_url || img.image_url;
          // Keep every card the same width and let its height follow the
          // image's true aspect ratio via the CSS ``aspect-ratio``
          // property, so the photo fills the card edge-to-edge without
          // being cropped: portrait/long sources grow taller, landscape
          // sources stay short. Until the load event fires we don't have
          // dimensions, so we fall back to the breakpoint's wide
          // rectangle ratio. The ratio snaps once on first load and
          // never changes again, so this can't oscillate or flicker
          // mid-marquee. Because the card's ratio matches the image's,
          // ``object-fit: cover`` no longer crops anything.
          const ratio = src ? imageAspectRatios[src] : undefined;
          const aspect = cardAspectRatio(ratio, config);
          const { className: filterClass, style: filterStyle } =
            getHeroImageStyle(img);
          return (
            <div
              key={key}
              className={`hero-gallery-card relative ${isFlush ? "hero-gallery-card--flush" : ""}`}
              style={{
                width: `${config.cardW}px`,
                aspectRatio: `${aspect}`,
              }}
            >
              {src ? (
                <ProgressiveHeroImage
                  src={src}
                  alt={img.alt_text || ""}
                  loading="lazy"
                  draggable={false}
                  className={filterClass}
                  style={filterStyle}
                  onLoad={handleImageLoad(src)}
                />
              ) : (
                <div className="hero-gallery-card__empty" />
              )}
              <EditableSlotMediaOverlay slotData={img} heroItem={heroItem} />
            </div>
          );
        })}
        {/* Optional "+ Add Image" card appended to the static stack
            in edit mode when the gallery is partially populated.
            Sized identically to a real photo card so it reads as
            "the next slot" rather than a chrome panel. The caller
            decides when to provide it (see ``addImageCard`` in
            ``HeroGallery``); in published render mode the caller
            passes ``null`` and the card never renders. We never
            attach it during the marquee branch — by the time the
            track duplicates (>= 3 images) the user has crossed the
            "enough images for the loop to look intentional"
            threshold and the prompt is no longer needed. */}
        {addImageCard}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroGallery main renderer
// ---------------------------------------------------------------------------

const HeroGallery = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  // Edit-mode flag for the in-track "+ Add Image" card. Visitors
  // never see editor chrome, so we short-circuit the card entirely
  // (wrapper included) when ``EditContext`` isn't mounted — see the
  // ``addImageCard`` prop on ``GalleryTrack`` below for the full
  // reasoning.
  const isEditing = !!useContext(EditContext);
  // Flush variants share this renderer with the canonical Gallery
  // entries (see the dispatch comment in ``HeroAutomatic``). The
  // mode id is the only place we can read which finish the user
  // picked — the slot contract / locks / overrides are inherited
  // from the canonical sibling and so don't differ. We branch the
  // chrome (card corners, shadow, inter-card gap) on this flag and
  // keep the rest of the renderer identical.
  const isFlush = typeof item?.hero_mode === "string"
    && item.hero_mode.endsWith("_flush");
  // ``gallery_proximity`` is the visitor-driven cadence sibling of
  // ``gallery_animated`` — same composition + same marquee CSS, but
  // the renderer layers a ``mousemove`` listener on top that scales
  // the marquee's playbackRate by cursor distance (slow when close,
  // fast when far). The mode id is the only place we can read which
  // motion story the user picked because the slot contract / locks /
  // ambient class are all inherited from the animated parent and
  // therefore identical. ``useMarqueeProximityRate`` is internally
  // gated on the running CSS animation existing on the ref'd element,
  // so it's safe to wire the ref unconditionally and toggle the hook
  // via ``enabled`` — the listener is only attached when the
  // proximity variant is actually selected.
  const isProximityVariant =
    item?.hero_mode === "gallery_proximity";
  const trackRef = useRef(null);
  useMarqueeProximityRate(trackRef, isProximityVariant);
  const gallerySlotData = heroSlots?.getSlot("gallery");
  // ``max_count > 1`` slots serialize as an array (possibly empty); the
  // single-assignment shape stays as an object. Treat both as "has slot"
  // only when there's actually something to render. An empty array is
  // functionally the same as a null slot for rendering purposes.
  const gallerySlotItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const hasGallerySlot = gallerySlotItems.length > 0;
  const gallerySlotDef = getSlotDef(heroSlots?.modeDefinition, "gallery");
  const parsedGallerySlotMax = Number(gallerySlotDef?.max_count);
  const gallerySlotMax =
    Number.isFinite(parsedGallerySlotMax) && parsedGallerySlotMax > 0
      ? parsedGallerySlotMax
      : MIN_ITEMS_FOR_MARQUEE;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = config.horizontal && hasGallerySlot;
  // The hero root may carry user-configured vertical padding. For the
  // desktop Gallery composition the image column is meant to bleed to
  // the hero's top/bottom edges, so it needs the same vertical breakout
  // that Split's media column uses.
  const heroPad = useHeroVerticalPadding(heroBase.heroRootRef);
  const galleryBreakoutStyle = isHorizontal
    ? {
        marginTop: heroPad.top ? `-${heroPad.top}px` : 0,
        marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
      }
    : null;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Gallery's signature ambient animation is "marquee-y", shipped with
  // ``gallery_animated``. Attaches to the scrolling image track only —
  // the text column animates via entrance-only (fade-up by default) to
  // stay out of the way. Ambient is mode-owned, independent of the
  // entrance toggle.
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // Defensive fetch: if a legacy or racey payload gave us an
  // image_collection ref without the embedded ``images`` array, we
  // hydrate it on the fly. In the common case the backend already
  // embeds ``images`` (see ``HeroSerializer._build_minimal_child_ref``)
  // so this useQuery stays disabled and we don't make a network call.
  const legacyCollection = useMemo(() => {
    return gallerySlotItems.find(
      (it) => it?.type === "image_collection" && !Array.isArray(it?.images),
    ) || null;
  }, [gallerySlotItems]);

  const { data: fetchedCollection } = useQuery({
    queryKey: ["item", legacyCollection?.type, legacyCollection?.id],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/items/${legacyCollection.type}/${legacyCollection.id}/`,
      );
      if (!res.ok)
        throw new Error(`Failed to fetch image_collection ${legacyCollection.id}`);
      return res.json();
    },
    enabled: !!legacyCollection?.id,
    staleTime: 30_000,
  });

  // Flatten the heterogeneous slot contents into a uniform list of image
  // descriptors. ``image_collection`` entries contribute their inner
  // ``images`` (hydrated from the fetched payload if the ref was legacy);
  // ``image`` entries contribute themselves. Anything else is skipped.
  const resolvedImages = useMemo(() => {
    const out = [];
    for (const item of gallerySlotItems) {
      if (!item) continue;
      if (item.type === "image_collection") {
        const src = Array.isArray(item.images)
          ? item.images
          : fetchedCollection && fetchedCollection.id === item.id
            ? fetchedCollection.images
            : [];
        for (const img of src || []) out.push(img);
      } else if (item.type === "image") {
        out.push(item);
      }
    }
    return out;
  }, [gallerySlotItems, fetchedCollection]);

  // ─── Mobile dispatch → HeroGalleryStrip ────────────────────────────
  //
  // The Gallery family's signature composition is "text on one side,
  // vertical marquee column on the other". That works at desktop /
  // tablet widths where the hero has horizontal real estate to spare,
  // but on phones it collapses awkwardly: the gallery column has to
  // shrink to a thin sliver next to the text, or stack on top with
  // a fixed-height row of cards rolling sideways inside a scene that
  // was originally designed for the vertical-scroll variant.
  //
  // Gallery Strip already solves the "narrow viewport" case natively:
  // photos scroll horizontally across the top in a continuous marquee
  // and the title / body / CTA stack beneath. That's exactly the
  // shape we want on mobile, and the renderer is already battle-tested
  // for that breakpoint band.
  //
  // So at ``small`` / ``extra_small`` we hand off to ``HeroGalleryStrip``
  // entirely. Flush identity is preserved automatically — both renderers
  // detect flush via ``item.hero_mode.endsWith("_flush")``, so
  // ``gallery_flush`` / ``gallery_animated_flush`` map cleanly to the
  // strip's own flush chrome (square-cornered, gapless cards) and
  // their non-flush siblings stay rounded with the configured gap.
  //
  // Two adjustments to ``heroSlots`` make the handoff seamless:
  //
  //   1. ``ambientAnimation`` is rewritten from ``marquee-y`` (the
  //      vertical scroll keyframes Gallery uses) to ``marquee-x`` (the
  //      horizontal scroll keyframes Gallery Strip uses). Without this
  //      rewrite the strip would mount the wrong CSS animation and
  //      the cards would translate vertically inside a horizontal-row
  //      track, which produces no visible motion. ``gallery`` and
  //      ``gallery_flush`` (no ambient) pass through unchanged so the
  //      strip renders its static fallback row.
  //
  //   2. ``getSlot("gallery")`` is overridden to return the already-
  //      flattened ``resolvedImages`` list with each entry tagged
  //      ``type: "image"``. ``HeroGalleryStrip`` only knows about
  //      ``image`` items (its slot contract doesn't include
  //      ``image_collection``), so handing it a legacy
  //      ``image_collection`` ref directly would result in zero rendered
  //      cards on mobile. Doing the flattening here — after
  //      ``resolvedImages`` has hydrated any legacy collection — keeps
  //      mobile rendering correct for sites authored before the slot
  //      contract change.
  //
  // The proximity hook (``useMarqueeProximityRate``) is intentionally
  // not threaded through to the strip path: cursor proximity has no
  // analogue on a touch device, so falling back to the strip's own
  // continuous marquee is the right behaviour for the
  // ``gallery_proximity`` variant on phones.
  const isMobile = bp === "small" || bp === "extra_small";
  const mobileHeroSlots = useMemo(() => {
    if (!isMobile || !heroSlots) return null;
    const stripImages = resolvedImages.map((img) =>
      img?.type === "image" ? img : { ...img, type: "image" },
    );
    return {
      ...heroSlots,
      ambientAnimation:
        heroSlots.ambientAnimation === "marquee-y"
          ? "marquee-x"
          : heroSlots.ambientAnimation,
      getSlot: (slotName) =>
        slotName === "gallery" ? stripImages : heroSlots.getSlot(slotName),
      hasSlot: (slotName) =>
        slotName === "gallery"
          ? stripImages.length > 0
          : heroSlots.hasSlot(slotName),
    };
  }, [isMobile, heroSlots, resolvedImages]);

  if (isMobile && mobileHeroSlots) {
    return (
      <HeroGalleryStrip
        item={item}
        heroBase={heroBase}
        heroSlots={mobileHeroSlots}
        forceCenteredContent
      />
    );
  }

  // Both branches render the same ``<Background>`` layer so the hero's
  // own color / pattern / image (set via the Style panel) is always
  // visible. The gallery track below sits on top and only covers its
  // own column; the text column shows the hero background through.
  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // No gallery slot filled → behave like Split-with-no-media: stacked
  // single-column layout so the text still reads cleanly. This is also
  // what the edit UI sees when the user first switches to Gallery mode
  // before assigning images.
  if (!hasGallerySlot) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${isHorizontal ? "flex flex-row" : "flex flex-col"} w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Gallery column.
         *
         * The marquee track inside can hold an arbitrary number of
         * cards — when it has many it overflows vertically. The
         * column itself, however, MUST NOT size to that overflowing
         * content, otherwise the hero grows unbounded with every
         * image the user adds (the user-reported bug: "the size of
         * the hero gets bigger without the need for it to actually
         * get bigger").
         *
         * The two-line trick below pins the column to the row's
         * cross-axis size instead of its content's intrinsic size:
         *   1. ``minHeight: 0`` overrides flexbox's implicit
         *      ``min-height: auto`` for flex items, which otherwise
         *      forces an item to be at least as tall as its content.
         *   2. The ``GalleryTrack`` (and the empty placeholder) is
         *      wrapped in an absolutely-positioned ``inset-0`` div so
         *      it's pulled out of the column's flow entirely — it
         *      can't push the column past the row's height anymore.
         *
         * With both in place, the column stretches naturally via the
         * default ``align-items: stretch`` to whatever height the
         * row settles on (driven by ``minHeight: baseHeightPx`` plus
         * the text column's content). The marquee track scrolls
         * inside that stretched column and is clipped by the
         * ``overflow: hidden`` we already have on the wrapper.
         */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={
            isHorizontal
              ? { width: config.mediaPct, minHeight: 0, ...(galleryBreakoutStyle || {}) }
              : { width: "100%", height: `${config.cardH + 40}px` }
          }
        >
          <div className="absolute inset-0 overflow-hidden">
            {resolvedImages.length > 0 ? (
              <GalleryTrack
                images={resolvedImages}
                ambientClass={ambientClass}
                config={config}
                isHorizontal={isHorizontal}
                isFlush={isFlush}
                trackRef={trackRef}
                heroItem={item}
                /* Keep the "+ Add Image" card available while the
                   slot still has capacity, but let the shared edit
                   affordance CSS reveal it only when the hero is
                   hovered/focused. This keeps partially populated
                   galleries discoverable without reserving a visible
                   blank card at rest.
                   ``EmptyMediaSlotAffordance`` is gated on
                   ``EditContext`` internally, so this is a no-op for
                   visitors viewing the published site. The card is
                   appended *after* the existing photo cards (so it
                   reads as "the next slot") and matches their pixel
                   dimensions / flush chrome so it sits flush in the
                   rotation. ``order`` is the next free position so
                   the new image lands at the end of the gallery
                   slot. */
                addImageCard={
                  // Gate the whole card on edit mode so visitors
                  // never see the empty wrapper. ``EmptyMediaSlotAffordance``
                  // already returns ``null`` outside the editor, but
                  // its parent ``<div class="hero-gallery-card">``
                  // would still render — leaving a phantom blank card
                  // in the published track. Checking ``EditContext``
                  // here too keeps the card's structural chrome out of
                  // the visitor DOM entirely.
                  isEditing && resolvedImages.length < gallerySlotMax ? (
                    <div
                      className={`hero-gallery-card hero-edit-add-card-affordance ${
                        isFlush ? "hero-gallery-card--flush" : ""
                      }`}
                      style={{
                        width: `${config.cardW}px`,
                        height: `${config.cardH}px`,
                        "--hero-edit-add-card-width": `${config.cardW}px`,
                        "--hero-edit-add-card-height": `${config.cardH}px`,
                        // The default card chrome (white background,
                        // hairline border, drop shadow) competes with
                        // the dashed-outline button inside. Strip it
                        // so the affordance reads as a "ghost slot"
                        // rather than a real card with a button on
                        // top.
                        background: "transparent",
                        border: 0,
                        boxShadow: "none",
                      }}
                    >
                      <EmptyMediaSlotAffordance
                        heroSlots={heroSlots}
                        heroId={item?.id}
                        slotName="gallery"
                        order={resolvedImages.length}
                        fill
                      />
                    </div>
                  ) : null
                }
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-white/40">
                Add images to the gallery
              </div>
            )}
          </div>
        </div>

        {/* Text column. Same spacing rules as Split. ``min-w-0`` is
            required so the column honours its ``flex-1`` allocation
            instead of growing to fit a wide child (button row, long
            word, etc.) and pushing text past the visible hero. See
            the matching comment in HeroSplit for the full reasoning. */}
        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
          style={{
            ...(isHorizontal
              ? { paddingLeft: config.contentPx, paddingRight: config.contentPx }
              : { paddingLeft: config.contentPx, paddingRight: config.contentPx, paddingTop: config.contentPy, paddingBottom: config.contentPy }),
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroGallery);
